/**
 * useTokenStatus Hook
 *
 * Monitor OAuth token expiry and automatically refresh when needed.
 * Provides UI components with token status information.
 */

import { useState, useEffect } from 'react';
import {
  isTokenExpired,
  getTokenTimeRemaining,
  onTokenRefresh,
  refreshAccessToken,
} from '../lib/firebase';

interface TokenStatus {
  isExpired: boolean;
  timeRemaining: number; // milliseconds
  isRefreshing: boolean;
  lastRefreshError: Error | null;
}

export const useTokenStatus = () => {
  const [status, setStatus] = useState<TokenStatus>({
    isExpired: false,
    timeRemaining: 0,
    isRefreshing: false,
    lastRefreshError: null,
  });

  // Update token status every minute
  useEffect(() => {
    const updateStatus = () => {
      setStatus(prev => ({
        ...prev,
        isExpired: isTokenExpired(),
        timeRemaining: getTokenTimeRemaining(),
      }));
    };

    // Initial update
    updateStatus();

    // Update every minute
    const interval = setInterval(updateStatus, 60 * 1000);

    // Subscribe to token refresh events
    const unsubscribe = onTokenRefresh((token) => {
      setStatus(prev => ({
        ...prev,
        isExpired: !token,
        timeRemaining: getTokenTimeRemaining(),
        isRefreshing: false,
        lastRefreshError: token ? null : prev.lastRefreshError,
      }));
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  // Manual refresh function
  const manualRefresh = async () => {
    setStatus(prev => ({ ...prev, isRefreshing: true, lastRefreshError: null }));

    try {
      const newToken = await refreshAccessToken();

      if (!newToken) {
        throw new Error('Failed to refresh token');
      }

      setStatus(prev => ({
        ...prev,
        isRefreshing: false,
        isExpired: false,
        timeRemaining: getTokenTimeRemaining(),
        lastRefreshError: null,
      }));

      return true;
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        isRefreshing: false,
        lastRefreshError: error as Error,
      }));

      return false;
    }
  };

  return {
    ...status,
    manualRefresh,
    // Helper computed properties
    isExpiringSoon: status.timeRemaining < 10 * 60 * 1000, // < 10 minutes
    minutesRemaining: Math.floor(status.timeRemaining / 1000 / 60),
  };
};
